/**
 * Supabase Finished Content API Service
 *
 * Handles CRUD operations for finished marketing creatives
 * (saved ads/content with title, thumbnail, and caption).
 */

import { supabase } from './client';
import type { FinishedContentRow, FinishedContentInsert } from './database.types';

// Frontend FinishedContent type (camelCase)
export interface FinishedContentData {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  thumbnailUrl: string | null;
  caption: string | null;
}

// Map database row to frontend format
const mapRowToData = (row: FinishedContentRow): FinishedContentData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  title: row.title,
  thumbnailUrl: row.thumbnail_url,
  caption: row.caption,
});

// Map frontend data to database format
const mapDataToRow = (data: Omit<FinishedContentData, 'id' | 'createdAt' | 'updatedAt'>): FinishedContentInsert => ({
  title: data.title,
  thumbnail_url: data.thumbnailUrl,
  caption: data.caption,
});

class FinishedContentApi {
  async getAll(): Promise<FinishedContentData[]> {
    const { data, error } = await supabase
      .from('finished_content')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapRowToData);
  }

  async getById(id: string): Promise<FinishedContentData> {
    const { data, error } = await supabase
      .from('finished_content')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return mapRowToData(data as FinishedContentRow);
  }

  async create(contentData: { title: string; thumbnailUrl?: string | null; caption?: string | null }): Promise<FinishedContentData> {
    const dbData: FinishedContentInsert = {
      title: contentData.title,
      thumbnail_url: contentData.thumbnailUrl || null,
      caption: contentData.caption || null,
    };

    const { data, error } = await supabase
      .from('finished_content')
      .insert(dbData as any)
      .select()
      .single();

    if (error) throw error;
    return mapRowToData(data as FinishedContentRow);
  }

  async update(id: string, updates: Partial<{ title: string; thumbnailUrl: string | null; caption: string | null }>): Promise<FinishedContentData> {
    const dbUpdates: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.thumbnailUrl !== undefined) dbUpdates.thumbnail_url = updates.thumbnailUrl;
    if (updates.caption !== undefined) dbUpdates.caption = updates.caption;

    const { data, error } = await supabase
      .from('finished_content')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapRowToData(data as FinishedContentRow);
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('finished_content')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

export const finishedContentApi = new FinishedContentApi();
